import React, { useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import NextLessonCountdown from './NextLessonCountdown';
import LessonFeedback from './LessonFeedback';
import LessonUpdatedChip from './LessonUpdatedChip';
import LessonWatchedToggle from './LessonWatchedToggle';
import LessonSteps from './LessonSteps';
import useLessonTracking from '../useLessonTracking';
import { currentHelpful, isLessonCompleted, isLessonUpdated } from '../useTutorialProgress';

const gradientButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '999px',
  px: 2.5,
  background: `linear-gradient(135deg, ${ColorPalette.blueMain} 0%, #4A6BFF 100%)`,
  boxShadow: '0 8px 20px rgba(90, 128, 251, 0.28)',
  '&:hover': { background: `linear-gradient(135deg, #4A6BFF 0%, ${ColorPalette.blueMain} 100%)` },
  '&.Mui-disabled': { background: ColorPalette.bgMedium, boxShadow: 'none' }
};

// Legenda da narração (arquivo .vtt da lição), ligada por padrão; a pessoa desliga no botão do player
const captionCueSx = {
  backgroundColor: 'rgba(26, 26, 46, 0.78)',
  color: '#FFFFFF',
  fontFamily: 'inherit',
  fontSize: '0.95em',
  lineHeight: 1.4
};

/**
 * Player da lição: cabeçalho compacto, vídeo, passo a passo e navegação.
 * @param {Object} lesson - { title, summary, steps, status, media: { src, poster, captions }, route }
 * @param {Number} index - posição da lição no capítulo (0-based)
 * @param {Number} total - número de lições do capítulo
 * @param {Function} onPrev
 * @param {Function} onNext
 * @param {Function} onOpenScreen - abre a tela do app ligada à lição (opcional)
 * @param {String} nextTitle - título da próxima lição, mostrado na contagem do fim do vídeo
 * @param {Number} playToken - muda a cada lição escolhida; reinicia o vídeo do começo
 * @param {Boolean} autoPlay - toca sozinho (depois que a pessoa escolheu uma lição)
 * @param {Object} progress - progresso salvo da lição (useTutorialProgress)
 * @param {Function} onTrack - track() de useTutorialProgress; sem ele o progresso não é gravado
 * @param {String} source - 'page' (página Tutoriais) ou 'help' (atalho "Como usar esta tela")
 * @param {Boolean} showSteps - mostra o passo a passo abaixo do vídeo (a página Tutoriais o põe ao lado)
 */
const LessonPlayer = ({
  lesson, index, total, onPrev, onNext, onOpenScreen, nextTitle, playToken = 0, autoPlay = false,
  progress = null, onTrack = null, source = 'page', showSteps = true
}) => {
  const media = lesson.media || null;
  const videoInProduction = lesson.status === 'em-producao';
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  // A contagem pertence ao vídeo que terminou; trocar de lição a descarta no mesmo render
  const playKey = `${lesson.id}-${playToken}`;
  const [countdownFor, setCountdownFor] = useState(null);
  const countdown = countdownFor === playKey;
  const tracking = useLessonTracking({ lesson, playKey, progress, source, onTrack });
  const completed = isLessonCompleted(progress);
  const updated = isLessonUpdated(lesson, progress);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.25 }}>
        <Typography
          sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: ColorPalette.textLight }}
        >
          Lição {index + 1} de {total}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {videoInProduction && (
            <Chip
              size="small"
              label="Vídeo em produção"
              sx={{ backgroundColor: ColorPalette.warningLight, color: '#8A5A00', fontWeight: 600, fontSize: '0.72rem' }}
            />
          )}
          {onTrack && updated && <LessonUpdatedChip />}
          {onTrack && (
            <LessonWatchedToggle
              completed={completed}
              onToggle={(watched) => onTrack(lesson, watched ? 'watched' : 'unwatched')}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: { xs: '1.05rem', md: '1.15rem' }, fontWeight: 700, color: '#1A1A2E', lineHeight: 1.25, m: 0 }}>
          {lesson.title}
        </Typography>
        {lesson.summary && (
          <Typography sx={{ color: ColorPalette.textMedium, fontSize: '0.85rem', lineHeight: 1.5, mt: 0.25 }}>
            {lesson.summary}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${ColorPalette.border}`,
          backgroundColor: ColorPalette.bgMain,
          boxShadow: '0 16px 40px rgba(26, 26, 46, 0.08)',
          '& video::cue': captionCueSx
        }}
      >
        {media && media.type === 'image' && (
          <img src={media.src} alt={lesson.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
        {media && media.type !== 'image' && (
          <video
            key={`${media.src}-${playToken}`}
            src={media.src}
            poster={media.poster}
            preload="none"
            autoPlay={autoPlay}
            controls
            {...tracking}
            onPlay={() => {
              setCountdownFor(null);
              tracking.onPlay?.();
            }}
            onEnded={() => {
              tracking.onEnded?.();
              if (hasNext) setCountdownFor(playKey);
            }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          >
            {media.captions && <track kind="subtitles" src={media.captions} srcLang="pt-BR" label="Português" default />}
          </video>
        )}
        {countdown && hasNext && (
          <NextLessonCountdown key={playKey} nextTitle={nextTitle} onNext={onNext} onCancel={() => setCountdownFor(null)} />
        )}
      </Box>

      {onTrack && completed && (
        <LessonFeedback value={currentHelpful(lesson, progress)} onAnswer={(helpful) => onTrack(lesson, 'helpful', { helpful })} />
      )}

      {showSteps && <Box sx={{ mt: 3 }}><LessonSteps lesson={lesson} /></Box>}

      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          onClick={onPrev}
          disabled={!hasPrev}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, color: ColorPalette.textMedium, borderRadius: '999px' }}
        >
          Lição anterior
        </Button>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {lesson.route && onOpenScreen && (
            <Button
              variant="outlined"
              onClick={() => onOpenScreen(lesson.route)}
              endIcon={<OpenInNewIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', px: 2.5, borderColor: ColorPalette.border, color: ColorPalette.textDark }}
            >
              Abrir a tela
            </Button>
          )}
          <Button variant="contained" onClick={onNext} disabled={!hasNext} endIcon={<ArrowForwardIcon />} sx={gradientButtonSx}>
            Próxima lição
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LessonPlayer;
